import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { DataList } from '../data-list/data-list.class';
import { EnumDataListColumnType, IDataListColumn, IDataListOrderBy, DataListComponent } from '../data-list/data-list.component';
import { Subscription } from 'rxjs';
import { IUser } from '@simplyfly/shared';
import { AppDataService } from 'src/app/services/data/app-data.service';
import { EnumQuotationStatus, IQuotation } from '@simplyfly/shared';
import { getQuotationStatusLabel } from 'src/app/interfaces/quotation.helpers';
import { QuotationService } from 'src/app/services/quotations/quotations.service';
import { getLanguageLabel } from 'src/app/enums/language.helpers';
import { UserService } from 'src/app/services/user/user.service';
import { EnumAcl } from '@simplyfly/shared';
import { AclService } from 'src/app/services/acl.service';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from 'src/app/services/auth/auth.service';

@Component({
    selector: 'app-quotations-list-for-enquiry',
    templateUrl: './quotations-list-for-enquiry.component.html',
    styleUrls: ['./quotations-list-for-enquiry.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [DataListComponent]
})
export class QuotationsListForEnquiryComponent
  extends DataList<IQuotation>
  implements OnInit, OnChanges, OnDestroy
{
  @Input() enquiryId: string | null = null;

  EnumAcl = EnumAcl;

  faPlus = faPlus;

  usersObj: { [id: string]: IUser } = {};

  currentPagination = 'enquiry-quotations-list';
  columns: IDataListColumn[] = [
    {
      title: this.translateService.instant('GENERAL.VERSION'),
      field: 'quotationNumber',
      notSortable: true,
      type: EnumDataListColumnType.function,
      resolve: (quotation: IQuotation): string => {
        return this.getQuotationDisplayName(quotation);
      }
    },
    {
      title: this.translateService.instant('GENERAL.STATUS'),
      field: 'status',
      notSortable: true,
      type: EnumDataListColumnType.function,
      resolve: (quotation: IQuotation): string => {
        return getQuotationStatusLabel(quotation.status);
      }
    },
    {
      title: this.translateService.instant('GENERAL.LANGUAGE'),
      field: 'language',
      notSortable: true,
      type: EnumDataListColumnType.function,
      resolve: (quotation: IQuotation): string => {
        return getLanguageLabel(quotation.language);
      }
    },
    {
      title: this.translateService.instant('GENERAL.DATE_CREATED'),
      field: 'created',
      notSortable: true,
      type: EnumDataListColumnType.date
    },
    {
      title: this.translateService.instant('GENERAL.CREATED_BY'),
      field: 'createdBy',
      notSortable: true,
      type: EnumDataListColumnType.function,
      resolve: (item: IQuotation): string => {
        return item.createdBy && this.usersObj[item.createdBy]
          ? this.usersObj[item.createdBy].initials
          : '';
      }
    }
  ];

  orderBy: IDataListOrderBy | null = { field: 'title', direction: 'desc' };

  isLogged = false;

  private subscriptions = new Subscription();

  constructor(
    private authService: AuthService,
    private quotationService: QuotationService,
    private appDataService: AppDataService,
    private userService: UserService,
    private aclService: AclService,
    private translateService: TranslateService
  ) {
    super();

    this.setDataService(this.quotationService);
  }

  ngOnInit(): void {
    this.authService.isLoggedObservable.subscribe(
      (isLogged: boolean) => (this.isLogged = isLogged)
    );

    this.updatedEnquiryId();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.enquiryId && !changes.enquiryId.isFirstChange()) {
      this.updatedEnquiryId();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  updatedEnquiryId(): void {
    if (this.isLogged) {
      if (this.enquiryId) {
        this.conditions = [
          {
            field: 'enquiryId',
            operator: '==',
            value: this.enquiryId
          }
        ];

        this.resetAndLoad();
      }

      this.loadUsers();
    } else {
      setTimeout(() => {
        this.updatedEnquiryId();
      }, 500);
    }
  }

  loadUsers(): void {
    this.subscriptions.add(
      this.userService.getAll().subscribe((users: IUser[]) => {
        for (const user of users) {
          this.usersObj[user.id] = user;
        }
      })
    );
  }

  hasAclAccess(id: EnumAcl): boolean {
    return this.aclService.hasAclAccess(id);
  }

  getQuotationDisplayName(quotation: IQuotation | null | undefined): string {
    if (!quotation) {
      return '';
    }

    const numberPart = quotation.quotationNumber || '';
    const titlePart = quotation.title || '';

    if (numberPart && titlePart && titlePart !== numberPart) {
      return `${numberPart} - ${titlePart}`;
    }

    return numberPart || titlePart;
  }
}
